'use client';

import { createContext, useCallback, useContext, useEffect, useRef, useState, type CSSProperties } from 'react';
import { usePathname } from 'next/navigation';
import { baseUiScale, fitUiScale, parseUiScaleMode, SCALE_MOBILE_BREAKPOINT, type UiScaleMode } from '@/lib/ui-scale';

const STORAGE_KEY = 'otan-ui-scale';
interface UiScaleValue { mode: UiScaleMode; scale: number; setMode: (mode: UiScaleMode) => void }
const UiScaleContext = createContext<UiScaleValue>({ mode: 'auto', scale: 1, setMode: () => {} });

/** User preference and scale are confined to the dashboard, not auth pages. */
export function UiScaleProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const root = useRef<HTMLDivElement>(null);
  const [mode, setModeState] = useState<UiScaleMode>('auto');
  const [scale, setScale] = useState(1);
  const [ready, setReady] = useState(false);
  const scaleRef = useRef(1);

  useEffect(() => {
    let saved: string | null = null;
    try { saved = localStorage.getItem(STORAGE_KEY); } catch { /* Storage can be disabled. */ }
    // Browser storage is unavailable during SSR; read it after hydration.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setModeState(parseUiScaleMode(saved));
    setReady(true);
    const storage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) setModeState(parseUiScaleMode(event.newValue));
    };
    window.addEventListener('storage', storage);
    return () => window.removeEventListener('storage', storage);
  }, []);

  const setMode = useCallback((value: UiScaleMode) => {
    const next = parseUiScaleMode(value);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* The choice still works for this page. */ }
    setModeState(next);
  }, []);

  useEffect(() => {
    if (!ready || !root.current) return;
    const element = root.current;
    let frame = 0;
    let disposed = false;
    const fit = () => {
      frame = 0;
      if (disposed) return;
      const viewport = document.documentElement.clientWidth || window.innerWidth;
      let next = mode === 'auto' ? baseUiScale(viewport) : Number(mode) / 100;
      // Measure at the baseline and fit synchronously within one animation
      // frame. This permits the scale to grow again when a wide table is
      // replaced, without showing the intermediate size to the user.
      element.style.zoom = String(next);
      const main = element.querySelector('main');
      if (mode === 'auto' && viewport >= SCALE_MOBILE_BREAKPOINT && main) {
        for (let i = 0; i < 8 && main.clientWidth; i++) {
          const boxes = Array.from(main.querySelectorAll<HTMLElement>('.overflow-x-auto, .overflow-x-scroll, .overflow-auto'))
            .filter((box) => !box.closest('[role="dialog"], dialog, [aria-modal="true"]') && box.getClientRects().length > 0)
            .map((box) => ({ clientWidth: box.clientWidth, scrollWidth: box.scrollWidth }));
          // Also catch page-level overflow from forms or fixed-width elements
          // that do not live inside an explicit scroll container.
          boxes.push({ clientWidth: element.clientWidth, scrollWidth: element.scrollWidth });
          const fitted = fitUiScale(viewport, next, main.clientWidth, boxes);
          if (fitted >= next - 0.0005) break;
          next = fitted;
          element.style.zoom = String(next);
        }
      }
      if (Math.abs(scaleRef.current - next) >= 0.0005) {
        scaleRef.current = next;
        setScale(next);
      }
    };
    const schedule = () => { if (!frame && !disposed) frame = window.requestAnimationFrame(fit); };
    schedule();
    window.addEventListener('resize', schedule);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    observer?.observe(element);
    const main = element.querySelector('main');
    if (main) observer?.observe(main);
    const mutations = typeof MutationObserver === 'undefined' ? null : new MutationObserver(schedule);
    if (main) mutations?.observe(main, { subtree: true, childList: true, characterData: true });
    return () => {
      disposed = true;
      window.removeEventListener('resize', schedule);
      observer?.disconnect(); mutations?.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [mode, pathname, ready]);

  return <UiScaleContext.Provider value={{ mode, scale, setMode }}>
    <div ref={root} style={{ zoom: scale, '--otan-ui-viewport-width': `${100 / scale}vw`, '--otan-ui-viewport-height': `${100 / scale}dvh` } as CSSProperties} data-ui-scale={mode}>{children}</div>
  </UiScaleContext.Provider>;
}

export function UiScaleControl() {
  const { mode, scale, setMode } = useContext(UiScaleContext);
  return <label className="flex items-center gap-2 text-sm text-dark dark:text-white">
    <span className="hidden sm:inline">Масштаб</span>
    <select aria-label="Масштаб интерфейса" value={mode} onChange={(event) => setMode(parseUiScaleMode(event.target.value))}
      className="rounded-md border border-stroke bg-transparent px-2 py-1.5 text-sm dark:border-dark-3 dark:bg-gray-dark">
      <option value="auto">Автоматически ({Math.round(scale * 100)}%)</option>
      <option value="100">100%</option>
      <option value="90">90%</option>
      <option value="80">80%</option>
    </select>
  </label>;
}
